import type { FormVariant } from "@shared/types";
import { REQUIRED_GRIND } from "../constants";

/**
 * Merges variant drafts with product variants based on import status and bag size validation.
 *
 * Logic copied from client ProductSelectModal.tsx lines 149-176.
 *
 * Merge strategy:
 * 1. If draft variant's bag size is rejected -> preserve original variant with draft's selection state
 * 2. If variant is imported OR has same bag size issue -> partial merge (dates + selection only)
 * 3. Otherwise -> full merge from draft, preserving original status and grind
 *
 * @param productVariants - Original product variants
 * @param draftVariants - Draft variants to merge in
 * @returns Merged variant array
 */
export function handleDisplayVariantDrafts(productVariants: FormVariant[], draftVariants: FormVariant[]): FormVariant[] {
  return productVariants.map((variant) => {
    const draft = draftVariants.find((draft) => draft.id === variant.id);
    if (draft) {
      // Return variant when draft variant of same bag size is rejected
      const mappedBagSize = draft.mapDetectedBagSizeTo ?? draft.bagSize;
      const isDraftVariantOfSameBagSizeRejected = productVariants.some(
        (x) => x.bagSize === mappedBagSize && x.id !== draft.id && x.status === "Rejected"
      );
      if (isDraftVariantOfSameBagSizeRejected) {
        return {
          ...variant,
          isProductSelectedForImport: draft.isProductSelectedForImport,
        };
      }

      const isBagSizeOfNotDefaultGrindType = variant.hasValidDetectedGrindType && !variant.grind?.includes(REQUIRED_GRIND);

      return variant.isBagSizeVariantImported || (variant.isVariantHasSameBagSize && isBagSizeOfNotDefaultGrindType)
        ? {
            ...variant,
            startDate: draft.startDate,
            endDate: draft.endDate,
            isProductSelectedForImport: draft.isProductSelectedForImport,
          }
        : {
            ...draft,
            status: variant.status,
            grind: variant.grind,
            // Drafts pre-date PIM creation by definition and carry
            // stale import flags (no `isImportedLocalVariant`, an out-of-date
            // `isImportedRemote`, no PIM overlay fields). The variant on this
            // side of the merge is the up-to-date `mapToLocalProduct` output,
            // so the source-of-truth import flags must come from it — without
            // this, a freshly-flipped Live status reverts to Submitted on
            // every modal re-open.
            isImportedRemote: variant.isImportedRemote,
            isImportedLocalVariant: variant.isImportedLocalVariant,
            localGrindOverride: variant.localGrindOverride,
            inventoryQuantity: variant.inventoryQuantity,
          };
    }
    return variant;
  });
}
